/**
 * UI Store
 *
 * Global UI state using Zustand (sidebar, modals, notifications)
 */

import { create } from 'zustand';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Modals
  activeModal: string | null;
  modalData: unknown;

  // Notifications
  notifications: Notification[];

  // Loading
  globalLoading: boolean;

  // Actions - Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;

  // Actions - Modals
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;

  // Actions - Notifications
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Actions - Loading
  setGlobalLoading: (loading: boolean) => void;
}

let notificationId = 0;

export const useUIStore = create<UIState>((set) => ({
  // Initial State
  sidebarOpen: true,
  sidebarCollapsed: true, // Sidebar fechada (recolhida) por padrao
  activeModal: null,
  modalData: null,
  notifications: [],
  globalLoading: false,

  // Sidebar Actions
  toggleSidebar: () =>
    set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  toggleSidebarCollapsed: () =>
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

  // Modal Actions
  openModal: (modalId, data) =>
    set({ activeModal: modalId, modalData: data }),

  closeModal: () =>
    set({ activeModal: null, modalData: null }),

  // Notification Actions
  addNotification: (notification) => {
    const id = `notification-${++notificationId}`;
    const newNotification = { ...notification, id };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove after duration (default 5s)
    const duration = notification.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      }, duration);
    }
  },

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  clearNotifications: () => set({ notifications: [] }),

  // Loading Actions
  setGlobalLoading: (loading) => set({ globalLoading: loading }),
}));

// Convenience functions
export const notify = {
  success: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'success', title, message }),

  error: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'error', title, message }),

  warning: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'warning', title, message }),

  info: (title: string, message?: string) =>
    useUIStore.getState().addNotification({ type: 'info', title, message }),
};
